/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-bitwise */
import { HandType } from '@ps/web-poker-types';
import type { RecentTournament, TableStaticData } from '@ps/web-shared-api-types/v0/SwHomeGames';
import type { HgScheduledRingGame } from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import type { DeepNonNullable } from 'utility-types';

import type { Speed, Structure } from './formatters/game';
import type { TournGroupTypes } from './formatters/tournGroups';
import type { AttributeKey, TableFlags2 } from './formatters/types';
import type { TestStateValues } from './utils/mtt.types';

export enum TournamentState {
  'ANNOUNCED' = 'ANNOUNCED',
  'CANCELLED' = 'CANCELLED',
  'COMPLETED' = 'COMPLETED',
  'FLIGHTED' = 'FLIGHTED',
  'FULL' = 'FULL',
  'LATE_REGISTRATION' = 'LATE_REGISTRATION',
  'PAUSED' = 'PAUSED',
  'PHASE' = 'PHASE',
  'REGISTERING' = 'REGISTERING',
  'RESTRICTED' = 'RESTRICTED',
  'RUNNING' = 'RUNNING',
  'SEATING' = 'SEATING'
}

export interface PokerGameTileAttribute {
  name: string;
  value: string;
  moneyIcon?: string;
  key: AttributeKey;
}

export interface PokerGameTileTag {
  title: string;
  color: string;
}

// clubId, server, serverObj
export interface PokerGameTile {
  attributes: PokerGameTileAttribute[];
  iconUrl: string;
  id: string;
  clubId: number;
  imageUrl: string;
  imageUrlV2?: string;
  fallbackImageUrl?: string;
  lastEntry?: string | null;
  link: string;
  tags: PokerGameTileTag[];
  type: string;
  server: string;
  serverObj: string;
  ePoolBlockGameType?: number;
  isPlayMoney?: boolean;
  mobileName?: string;
  tournGroup?: number;
  hasVideoSupport?: boolean;
  tournamentState?: TournamentState;
}

export enum Languages {
  en,
  'en-GB',
  'en-US',
  'en-IN',
  pl,
  nl,
  ja,
  it,
  fr,
  cs,
  et,
  bg,
  da,
  de,
  es,
  fi,
  hr,
  hu,
  ko,
  lt,
  lv,
  no,
  pt,
  ro,
  ru,
  sl,
  sv,
  uk,
  el,
  cz,
  'pt-br',
  'es-419',
  'zh-cn',
  'zh-tw',
  sk
}

export type Language = keyof typeof Languages;

export interface Winners {
  amount: number;
  flag: number;
}

export interface Prizes {
  min: number;
  max: number;
  numTickets: number;
  winners: Winners[];
}

export enum GameCategoryTypes {
  None = 0,
  Cash = 1 << 0,
  MTT = 1 << 1,
  SNG = 1 << 2,
  SAG = 1 << 3,
  Zoom = 1 << 4,
  SFN = 1 << 5,
  Table = 1 << 6, // Currently only used for MSG_MLOBBYNG_GET_GAME_INFO
  PPP = 1 << 7,
  Last = GameCategoryTypes.PPP
}

export interface ServerResponseAttributes {
  buyIn: number;
  rake: number;
  currency: number;
  maxPlayers: number;
  isPlayMoney: boolean;
  scalePM: number;
  prizes: Prizes[];
}

export interface MappedRawData {
  tourns: PokerGameTile[];
  tableTypes: PokerGameTile[];
  sngTypes: PokerGameTile[];
  sagTypes: PokerGameTile[];
  zoomTypes: PokerGameTile[];
  seatFinders: PokerGameTile[];
  pppTypes: PokerGameTile[];
}

export interface PlayerPoolBlock {
  gameType: number;
  blockedUntil: number;
  activeInfractionsNum: number;
}

// taken from server side collection ePoolBlockGameType
export enum ePoolBlockGameTypeCollection {
  tableType = 1,
  seatFinder = 2,
  zoomType = 3
}

export interface BuyInTier {
  buyInTierId: number;
  buyIn: number;
  rake: number;
  potentialMaxKOWinToDisplay: number;
}

export interface Options {
  language: Language;
  disabledGameTypes: GameCategoryTypes[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hspFlagsMap: any;
  isPPPRevampNeymar: boolean;
  timeZone?: string;
}

export interface GameTypeUrl {
  holdem: string;
  flash: string;
  omaha: string;
  max: string;
  'holdem-six': string;
}

export interface SagGame {
  id: number;
  scriptId: number;
  game: string;
  isUltra: boolean;
  imageUrl: GameTypeUrl;
  isFlash: boolean;
  isMax: boolean;
  isHoldemSix?: boolean;
  buyIn: string;
  players: string;
  winUpTo: string;
  promoRef: string;
  payoutAmount?: string;
  payoutAmountSelected?: string;
  tournSites?: number;
  isEnabled?: boolean;
}

export interface getStakeProps {
  buyIn: number;
  currency: number;
  maxWinnerPayout: number;
  promoRef: string;
  rake: number;
  scalePM: number;
  isPlayMoney: boolean;
}

export const legacyHandTypeToAssetNameMap: Record<HandType, string> = {
  [HandType.NONE]: 'None',
  [HandType.BADUGI]: 'Badugi',
  [HandType.HOLDEM]: 'Holdem',
  [HandType.OMAHA_5]: '5 Omaha',
  [HandType.OMAHA]: 'Omaha',
  [HandType.FIVE_CARD_DRAW]: 'Draw',
  [HandType.SINGLE_DRAW_2_7]: 'Single Draw 27',
  [HandType.STUD]: 'Stud',
  [HandType.RAZZ]: 'Razz',
  [HandType.TRIPLE_DRAW_2_7]: '3 Draw 27',
  [HandType.TRIPLE_DRAW_A_5]: '3 Draw A5',
  [HandType.COURCHEVEL]: 'Courchevel',
  [HandType.OMAHA_6]: '6 Omaha',
  [HandType.SPLIT_HOLDEM]: 'Split Holdem',
  [HandType.SPLIT_OMAHA]: 'Split Omaha',
  [HandType.SHOWTIME_HOLDEM]: 'Showtime Holdem',
  [HandType.SHOWTIME_OMAHA]: 'Showtime Omaha',
  [HandType.FUSION]: 'Fusion',
  [HandType.UNFOLD_CLASSIC]: 'Unfold Classic',
  [HandType.UNFOLD_MAX]: 'Unfold Max',
  [HandType.SIX_PLUS_HOLDEM]: '6+ Holdem',
  [HandType.DEEPWATER_HOLDEM]: 'Deep Water Holdem',
  [HandType.DEEPWATER_OMAHA]: 'Deep Water Omaha',
  [HandType.SWAP_HOLDEM]: 'Swap Holdem',
  [HandType.ALL_IN_POKER]: 'All-In Poker',
  [HandType.TURMOIL_HOLDEM]: '', // undefined
  [HandType.HOSE]: 'Hose',
  [HandType.HORSE]: 'Horse',
  [HandType.EIGHT_GAME]: 'Nl 8-Game',
  [HandType.MIXED_HOLDEM]: 'Mixed Holdem',
  [HandType.MIXED_OMAHA]: 'Mixed Omaha Hl',
  [HandType.POT_LIMIT_HOLDEM_OMAHA]: 'Pl Holdem Pl Omaha',
  [HandType.TRIPLE_STUD]: 'Nl Triple Stud',
  [HandType.NO_LIMIT_HOLDEM_OMAHA]: 'Nl Holdem Pl Omaha',
  [HandType.OMAHA_MIXED]: 'Nl Mixed Omaha'
};

// Common props
interface BaseStaticData {
  id: string;
  clubId: number;
  currency: number;
  game: HandType;
  structure: number;
  isHiLo: boolean;
  server: string;
  serverObj: string;
  scalePM: number;
}

interface TableStakes {
  blinds: number[];
  stakes: number[];
}

interface CommonStaticData extends BaseStaticData {
  minChipsLimit: number;
  maxBuyIn: number;
  maxPlayersPerTable: number;
  ante: number;
  cap: number;
  tableStakes: TableStakes;
  loBet: number;
  hiBet: number;
}

interface RawGameInformation extends Omit<BaseStaticData, 'id'> {
  id: number;
  buyin: number;
  rake: number;
  active: boolean;
  scriptId: number;
  flags: number;
  brandMask: number;
  tournSites: number;
  speed: number;
  minPrizePool: number;
  maxPrizePool: number;
  maxPayout: number;
  promoRef: string;
  minClientVersion: number;
  maxClientVersion: number;
  mods: { numUsers: number }[];
  ticketTypes: unknown[];
  customSpeedDisplayNames: unknown;
  countryRestrictionInverted: boolean;
  countryRestrictions: unknown[];
  visibilityCriteria: string;
  mobileNames: unknown;
  tournGroup: number;
  admissions: string[];
  tournFlags: number;
  pokerNgGroup: string;
  tournGroupRef: string;
  tournSitesEx: string;
  eligibleBrands: unknown[];
  tournFlags2: number;
  tournGroupName: string;
  sagGroups: unknown[];
  timeBank: number;
  modLevels: unknown[];
  additionalVariants: number;
  flashModType: number;
  flashModTimeBank: number;
  vars: unknown[];
  sagPromoTicketShortText: {
    texts: Record<string, string>;
  };
  sagPromoTicketLongText: string;
  lobbyNewsContentData?: null;
}

export interface MapRawDataType {
  tourns: RecentTournament[];
  tableTypes: TableType[];
  sngTypes: SngType[];
  sagTypes: SagType[];
  zoomTypes: ZoomType[];
  seatFinders: SeatFinderType[];
  pppTypes: PPPType[];
}

// Specific interfaces extending common properties
export interface RawGame {
  information: RawGameInformation;
  statistics?: Record<string, number | string>;
}

export type ScheduledTableType = TableStaticData & HgScheduledRingGame;

export type TableType = DeepNonNullable<ScheduledTableType> & {
  id: string;
  serverObj: string;
  lastEntry: string;
};

export interface SagType {
  staticData: BaseStaticData & {
    scriptId: number;
    buyin: number;
    maxPrizePool: number;
    rake: number;
    flags: number;
    isPlayMoney: boolean;
    speedDisplay: number;
    tournGroup: number;
  };
  lastEntry: string;
  ngSagFlags: number;
  lobbyNewsContentData?: { 'payout-amount': string };
}

export interface SngType {
  id: string;
  clubId: number;
  buyIn: number;
  currency: number;
  game: HandType;
  isHiLo: number;
  minPlayers: number;
  maxPerTable: number;
  rake: number;
  satelliteTarget: number;
  server: string;
  serverObj: string;
  speedDisplay: Speed;
  tournGroup: number;
  structure: Structure;
  tournGrouptournGroup: TournGroupTypes;
  lastEntry: string;
  tournFlags2: string;
  tournFlags: number;
  maxPlayers: number;
  isPlayMoney: number;
  scalePM: number;
  prizes: Prizes[];
  awardScheme: number;
  timedTournType: number;
  timedTournPlayersPercent: number;
}

export interface SeatFinderType {
  staticData: CommonStaticData & {
    flags2: TableFlags2;
    isPM: number;
  };
  lastEntry: string;
}

export interface ZoomType {
  staticData: CommonStaticData & {
    isOneOnOne: number;
    isPlayMoney: number;
  };
  lastEntry: string;
}

export interface PPPType {
  buyIn: number;
  lastEntry: string;
  staticData: {
    pppId: string;
    structure: number;
    isHiLo: number;
    handType: HandType;
    currency: string;
    isPlayMoney: number;
    maxPlayersPerTable: number;
    buyInTiers: BuyInTier[];
  };
  clubId?: number;
  server?: number;
  serverObj?: number;
}

export interface MttTypeGeneratorParams {
  tournamentId: number;
  buyIn: number;
  currency: string;
  game: HandType;
  guaranteedPrizePool: number;
  knockout: number;
  isHiLo: number;
  maxPerTable: number;
  rake: number;
  numReentries: number;
  satelliteTarget: number;
  speedDisplay: Speed;
  structure: Structure;
  tournGroup: TournGroupTypes;
  tournFlags: number;
  tournFlags2?: string;
  fakeStatus?: TestStateValues | number;
  isRestricted?: number;
  tournFlagsServInt?: number;
}

export interface SagTypeGeneratorParams {
  brandMask: number;
  tournFlags: number;
  game: HandType;
  structure: Structure;
  buyin: number;
  rake: number;
  currency: number;
  speedDisplay: Speed;
  minPrizePool: number;
  maxPrizePool: number;
  tournGroupRef: string;
  flags: number;
  ngSagFlags: number;
  tournGroup?: TournGroupTypes;
}

export interface SngTypeGeneratorParams {
  id: number;
  game: HandType;
  structure: Structure;
  buyIn: number;
  rake: number;
  knockout: number;
  currency: number;
  speedDisplay: Speed;
  satelliteTarget: number;
  tournGroup: TournGroupTypes;
  maxPerTable: number;
  tournFlags2: string;
  minPlayers: number;
  maxPlayers: number;
  prizes: [
    {
      winners: { amount: number; flags: number }[];
    }
  ];
}

export interface TableTypeGeneratorParams {
  id: number;
  cap: number;
  ante: number;
  currency: string;
  defaultBuyIn: number;
  maxBuyIn: number;
  game: HandType;
  maxPlayers: number;
  structure: Structure;
  tableStakes: {
    antes: [];
    blinds: string[];
    stakes: [];
    bringIns: [];
  };
  tableFlags2?: string;
}

export interface ZoomTypeGeneratorParams {
  id: number;
  currency: string;
  defaultBuyIn: number;
  maxBuyIn: number;
  game: HandType;
  ante: number;
  cap: number;
  maxPlayers: number;
  structure: Structure;
  tableStakes: {
    antes: unknown[];
    blinds: string[];
    stakes: unknown[];
    bringIns: unknown[];
  };
  isOneOnOne: number;
  minChipsLimit?: number;
}

export interface SeatFinderTypeGeneratorParams {
  game: HandType;
  structure: Structure;
  cap: number;
  ante: number;
  currency: string;
  minChipsLimit: number;
  maxBuyIn: number;
  flags2: number;
  isHiLo: number;
}
